// apps/client/src/pages/leads/edm/components/CampaignStatsPanel.tsx
//
// Plan-v3 core Slice E1 (edm_tracking): the campaign results panel used when
// tracking is switched on for the company. Same delivery counters as
// StatsPanel (which stays the flag-off panel, unchanged), plus approximate
// opens and clicks, the A/B outcome and "Resend to non-openers" when the
// campaign's tracking was frozen at schedule (getStats returns `engagement`
// only then). Polled every 60 s while sending. No chart, no export, no list
// of who opened.

import React, { useEffect, useState } from 'react';
import { CheckCircle, Hourglass, MailOpen, MailX, MousePointerClick, Repeat, Send, UserMinus } from 'lucide-react';
import StatCard from '../../../../components/common/StatCard';
import ConfirmDialog from '../../../../components/common/ConfirmDialog';
import { SkeletonStatCards } from '../../../../components/common/SkeletonLoader';
import { EDM_UI, edmErrorMessage } from '../../../../utils/edm';
import type { EdmEngagement, EdmStats } from '../../../../hooks/useEdmCampaigns';

const POLL_MS = 60_000;
const PENDING_SUBTITLE = 'Delivery tracking arrives soon';
const HOUR_MS = 3_600_000;
/** createResend window (server B3): parent completed at least 24 h and at most 30 days ago. */
export const RESEND_MIN_MS = 24 * HOUR_MS;
export const RESEND_MAX_MS = 30 * 24 * HOUR_MS;

function measured(v: number | null | undefined): { value: string; subtitle?: string } {
  return typeof v === 'number' ? { value: v.toLocaleString('en-AU') } : { value: '–', subtitle: PENDING_SUBTITLE };
}

const n = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);

/** "12.3%" of `base`, or '–' when there is nothing to divide by. */
export function rate(count: number, base: number): string {
  return base > 0 ? `${((count / base) * 100).toFixed(1)}%` : '–';
}

/** Why the resend is not available yet (or any more), or null when it is. */
export function resendBlockedReason(completedAt: string | null, now: number = Date.now()): string | null {
  const t = completedAt ? Date.parse(completedAt) : NaN;
  if (!Number.isFinite(t)) return 'Available once the campaign has finished.';
  if (now - t < RESEND_MIN_MS) return 'Available 24 hours after the campaign finished, so people have time to open it.';
  if (now - t > RESEND_MAX_MS) return 'Only available for 30 days after the campaign finished.';
  return null;
}

const BREAKDOWN: Array<{ key: keyof EdmStats; label: string }> = [
  { key: 'blockedSuppressed', label: 'Skipped: suppressed' },
  { key: 'blockedConsentWithdrawn', label: 'Skipped: consent withdrawn' },
  { key: 'blockedOutOfScope', label: 'Skipped: no longer in your offices' },
  { key: 'blockedSourceMissing', label: 'Skipped: source removed' },
  { key: 'failedInvalidRecipient', label: 'Failed: invalid address' },
  { key: 'failedProvider', label: 'Failed at provider' },
  { key: 'excludedInvalidEmail', label: 'Excluded at freeze: invalid email' },
  { key: 'unknown', label: 'Unknown (awaiting confirmation)' },
  { key: 'complained', label: 'Complaints' },
];

const EngagementSection: React.FC<{ engagement: EdmEngagement; sent: number }> = ({ engagement, sent }) => {
  const opens = n(engagement.uniqueOpens);
  const clicks = n(engagement.uniqueClicks);
  const links = (engagement.links ?? []).filter((l) => n(l.uniqueClicks) > 0);
  const variants = (['A', 'B'] as const).filter((v) => engagement.byVariant?.[v]);
  return (
    <div className="space-y-4" data-testid="edm-engagement">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <StatCard
          title="Opened (approximate)"
          value={opens.toLocaleString('en-AU')}
          subtitle={`${rate(opens, sent)} of sent`}
          icon={<MailOpen className="h-5 w-5" />}
        />
        <StatCard
          title="Clicked (approximate)"
          value={clicks.toLocaleString('en-AU')}
          subtitle={`${rate(clicks, sent)} of sent`}
          icon={<MousePointerClick className="h-5 w-5" />}
        />
      </div>
      {variants.length > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-700 text-left text-xs text-gray-400">
              <th className="py-1.5 font-medium">Subject test</th>
              <th className="py-1.5 text-right font-medium">Sent</th>
              <th className="py-1.5 text-right font-medium">Opened (approx.)</th>
              <th className="py-1.5 text-right font-medium">Clicked (approx.)</th>
            </tr>
          </thead>
          <tbody>
            {variants.map((v) => {
              const row = engagement.byVariant?.[v];
              const vSent = n(row?.sent);
              return (
                <tr key={v} className="border-b border-gray-800">
                  <td className="py-1.5 text-gray-300">
                    Subject {v}
                    {engagement.winner === v && <span className="ml-2 text-emerald-300">Winner</span>}
                  </td>
                  <td className="py-1.5 text-right text-gray-100">{vSent.toLocaleString('en-AU')}</td>
                  <td className="py-1.5 text-right text-gray-100">{rate(n(row?.uniqueOpens), vSent)}</td>
                  <td className="py-1.5 text-right text-gray-100">{rate(n(row?.uniqueClicks), vSent)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      {links.length > 0 && (
        <table className="w-full table-fixed text-sm">
          <thead>
            <tr className="border-b border-gray-700 text-left text-xs text-gray-400">
              <th className="py-1.5 font-medium">Link</th>
              <th className="w-32 py-1.5 text-right font-medium">Clicked (approx.)</th>
            </tr>
          </thead>
          <tbody>
            {links.map((l) => (
              <tr key={l.url} className="border-b border-gray-800">
                <td className="truncate py-1.5 text-gray-300" title={l.url}>
                  {l.url}
                </td>
                <td className="py-1.5 text-right text-gray-100">{n(l.uniqueClicks).toLocaleString('en-AU')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="text-xs text-gray-400">
        Opens and clicks are approximate: some mail apps open every email automatically, others block images, and security
        scanners can follow links. Each person counts once.
      </p>
    </div>
  );
};

const CampaignStatsPanel: React.FC<{
  campaignId: string;
  status: string;
  completedAt: string | null;
  getStats: (campaignId: string) => Promise<EdmStats>;
  /** Present when the caller may create a resend (tracking on; the server re-checks access). */
  createResend?: (parentId: string) => Promise<{ campaignId: string }>;
  onResendCreated?: (campaignId: string) => void;
}> = ({ campaignId, status, completedAt, getStats, createResend, onResendCreated }) => {
  const [stats, setStats] = useState<EdmStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmResend, setConfirmResend] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  // Re-render when the resend window opens (24 h) or closes (30 days); nothing else polls a completed campaign.
  const [clock, setClock] = useState(0);
  useEffect(() => {
    const t = completedAt ? Date.parse(completedAt) : NaN;
    if (status !== 'completed' || !Number.isFinite(t)) return undefined;
    const now = Date.now();
    const next = [t + RESEND_MIN_MS, t + RESEND_MAX_MS + 1].find((b) => b > now);
    if (next === undefined) return undefined;
    // setTimeout overflows above 2^31-1 ms (~24.8 days); re-arm from the next render instead.
    const timer = setTimeout(() => setClock((c) => c + 1), Math.min(next - now, 2_147_483_647));
    return () => clearTimeout(timer);
  }, [status, completedAt, clock]);

  useEffect(() => {
    let cancelled = false;
    const load = () =>
      getStats(campaignId)
        .then((s) => {
          if (!cancelled) {
            setStats(s);
            setError(null);
          }
        })
        .catch((err) => {
          if (!cancelled) setError(edmErrorMessage(err));
        });
    void load();
    const timer = status === 'sending' || status === 'ab_waiting' ? setInterval(load, POLL_MS) : null;
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [campaignId, status, getStats]);

  if (error) return <div className={EDM_UI.errorBanner}>{error}</div>;
  if (!stats) return <SkeletonStatCards count={5} />;
  if (!(stats.sent > 0)) return null;

  const delivered = measured(stats.delivered);
  const bounced = measured(stats.bounced);
  const unsubscribed = measured(stats.unsubscribed);
  const rows = BREAKDOWN.filter((r) => {
    const v = stats[r.key];
    return typeof v === 'number' && v > 0;
  });
  const engagement = stats.engagement ?? null;
  const showResend = Boolean(createResend) && status === 'completed' && engagement !== null;
  const resendBlocked = showResend ? resendBlockedReason(completedAt) : null;

  const resend = async () => {
    if (!createResend) return;
    setResending(true);
    setResendError(null);
    try {
      const { campaignId: childId } = await createResend(campaignId);
      onResendCreated?.(childId);
    } catch (err) {
      setResendError(edmErrorMessage(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className={EDM_UI.card} data-testid="edm-campaign-stats">
      <h2 className={EDM_UI.heading}>
        <Send className={EDM_UI.headingIcon} /> Results
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
        <StatCard title="Sent" value={stats.sent.toLocaleString('en-AU')} icon={<Send className="h-5 w-5" />} />
        <StatCard title="Delivered" value={delivered.value} subtitle={delivered.subtitle} icon={<CheckCircle className="h-5 w-5" />} />
        <StatCard title="Bounced" value={bounced.value} subtitle={bounced.subtitle} icon={<MailX className="h-5 w-5" />} />
        <StatCard title="Unsubscribed" value={unsubscribed.value} subtitle={unsubscribed.subtitle} icon={<UserMinus className="h-5 w-5" />} />
        <StatCard
          title="Remaining"
          value={(stats.queued + stats.pending).toLocaleString('en-AU')}
          icon={<Hourglass className="h-5 w-5" />}
        />
      </div>
      {engagement ? (
        <EngagementSection engagement={engagement} sent={stats.sent} />
      ) : (
        <p className="text-xs text-gray-400">Opens and clicks were not tracked for this campaign.</p>
      )}
      {rows.length > 0 && (
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r) => (
              <tr key={r.key} className="border-b border-gray-800">
                <td className="py-1.5 text-gray-300">{r.label}</td>
                <td className="py-1.5 text-right text-gray-100">{(stats[r.key] as number).toLocaleString('en-AU')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showResend && (
        <div className="space-y-2">
          <button
            type="button"
            className={EDM_UI.secondaryButton}
            disabled={resending || resendBlocked !== null}
            onClick={() => setConfirmResend(true)}
          >
            <Repeat className="h-4 w-4" /> Resend to non-openers
          </button>
          {resendBlocked && <p className="text-xs text-gray-400">{resendBlocked}</p>}
          {resendError && <p className="text-xs text-red-300">{resendError}</p>}
        </div>
      )}
      <p className="text-xs text-gray-400">
        Sent means our email provider accepted the email. Delivered and bounced come from the receiving mail servers.
      </p>
      <ConfirmDialog
        isOpen={confirmResend}
        title="Resend to non-openers?"
        message="We create a new draft for the people who neither opened nor clicked this campaign, re-checked for consent and unsubscribes when you freeze it. You can change the subject, then test and schedule it as usual."
        confirmLabel="Create resend draft"
        confirmVariant="primary"
        onCancel={() => setConfirmResend(false)}
        onConfirm={() => {
          setConfirmResend(false);
          void resend();
        }}
      />
    </div>
  );
};

export default CampaignStatsPanel;
